import cronstrue from 'cronstrue';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, IDisposable, inject, Loader, Modal, ModalHandle, TasksStore, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { BsChevronDoubleUp } from '@react-icons/all-files/bs/BsChevronDoubleUp';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { BsToggleOff } from '@react-icons/all-files/bs/BsToggleOff';
import { BsToggleOn } from '@react-icons/all-files/bs/BsToggleOn';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { LinkContainer } from 'react-router-bootstrap';
import { Button, ButtonGroup } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { FileSystemsStore } from './FileSystemsStore';
import { ManifestsStore } from './ManifestsStore';

export interface IManifestItemProps {
    readonly manifest: Mri.SoftwareUpdateManifests.SoftwareUpdateManifest;
}

@autoinject
@observer
export class ManifestItem extends React.Component<IManifestItemProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.manifestsStore.loadStatusAsync(this.props.manifest.softwareUpdateManifestGuid);
        } catch (e: any) {
            this.tasksStore.toastErrorTask('Error loading status', e);
        } finally {
            this.loading = false;
        }

        try {
            this.handle = await this.manifestsStore.connectionManager.connect();
            await this.manifestsStore.subscribeToManifestAsync(this.props.manifest.softwareUpdateManifestGuid);
        } catch (e: any) {
            console.error('SubscribeToManifest', e);
        }
    }

    public async componentWillUnmount() {
        this.handle?.dispose();
    }

    public render() {
        const manifest = this.props.manifest;

        return (
            <tr className={!manifest.active ? 'inactive-row' : ''}>
                <td className="align-middle">{manifest.name}</td>
                <td className="align-middle">
                    <div className="flex-items-center">
                        <Tooltip message={this.progressTooltip} delay={0}>
                            <LinkContainer
                                to={`/software-updates/manifests/${this.props.manifest.softwareUpdateManifestGuid}/status`}
                            >
                                <Button color="link">
                                    {this.status?.updated ?? '-'}/{this.status?.total ?? '-'}
                                </Button>
                            </LinkContainer>
                        </Tooltip>

                        <Loader loading={this.loading} showSpinner />
                    </div>
                </td>
                <td className="align-middle">
                    <strong className={this.updatingStatusClass}>{this.updatingStatus}</strong>
                    {this.updating && (
                        <Tooltip message="Updates in progress">
                            <BsChevronDoubleUp className="text-running text-pulse ps-2" />
                        </Tooltip>
                    )}
                </td>
                <td className="align-middle">
                    <i>{manifest.schedule ? cronstrue.toString(manifest.schedule) : ''}</i>
                    {manifest.schedule && (
                        <>
                            <br />
                            <i>By {manifest.schedule}</i>
                        </>
                    )}
                </td>
                <td className="align-middle">
                    <div className="d-block display-row-events">
                        {this.fileSystems.map((f, idx) => (
                            <span key={idx}>
                                {f}
                                <br />
                            </span>
                        ))}
                    </div>
                </td>
                <td className="align-middle">
                    <ButtonGroup className="py-1">
                        <Tooltip message="Status" delay={0}>
                            <LinkContainer
                                to={`/software-updates/manifests/${this.props.manifest.softwareUpdateManifestGuid}/status`}
                            >
                                <Button color="primary">
                                    <BsListUl className="" />
                                </Button>
                            </LinkContainer>
                        </Tooltip>

                        <Tooltip message="Edit Manifest" delay={0}>
                            <LinkContainer
                                to={`/software-updates/manifests/${this.props.manifest.softwareUpdateManifestGuid}/edit`}
                            >
                                <Button color="primary">
                                    <BsPencilSquare />
                                </Button>
                            </LinkContainer>
                        </Tooltip>

                        {this.props.manifest.active && (
                            <Tooltip message="Deactivate" delay={0}>
                                <Button color="success" onClick={this.deactivate}>
                                    <BsToggleOn />
                                </Button>
                            </Tooltip>
                        )}

                        {!this.props.manifest.active && (
                            <Tooltip message="Activate" delay={0}>
                                <Button color="secondary" onClick={this.activate}>
                                    <BsToggleOff />
                                </Button>
                            </Tooltip>
                        )}

                        <Tooltip message="Delete" delay={0}>
                            <Button color="danger" onClick={this.delete}>
                                <BsTrash />
                            </Button>
                        </Tooltip>

                        <Tooltip message="Update Now" delay={0}>
                            <Button color="secondary" onClick={this.updateNow}>
                                <BsChevronDoubleUp className="text-running" />
                            </Button>
                        </Tooltip>
                    </ButtonGroup>
                </td>

                <Modal
                    className="modal-md"
                    title="Confirm Delete"
                    modalHandle={this.confirmModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <p className="text-center">Are you sure you want to Delete {this.props.manifest.name}?</p>
                </Modal>
            </tr>
        );
    }

    @computed
    private get fileSystems() {
        return this.props.manifest.fileSystems.map(f => {
            const fS = this.fileSystemsStore.fileSystems.get(f);
            if (fS) return fS.fileName;
        });
    }

    @computed
    private get updatingStatus() {
        if (this.status == null) return 'Idle';
        else if (this.status.updated == this.status.total) return 'Up to Date';
        else if (this.status.updating > 0) return 'Updating';
        else if (this.status.scheduled > 0) return 'Scheduled';
        else if (this.status.errored > 0 && this.status.idle > 0 && this.props.manifest.active)
            return 'Updating with Errors';
        else if (this.status.errored > 0) return 'Idle with Errors';
        else if (this.status.idle > 0 && this.props.manifest.active) return 'Scheduling';
        else if (this.status.idle > 0) return 'Idle';
        else return '-';
    }

    @computed
    private get updatingStatusClass() {
        if (this.status == null) return '';
        else if (this.status.updated == this.status.total) return 'text-success';
        else if (this.status.updating > 0) return 'text-info';
        else if (this.status.scheduled > 0) return 'text-info';
        else if (this.status.errored > 0 && this.status.idle > 0 && this.props.manifest.active) return 'text-warning';
        else if (this.status.errored > 0) return 'text-danger';
        else if (this.status.idle > 0 && this.props.manifest.active) return 'text-info';
        else if (this.status.idle > 0) return '';
        else return '';
    }

    @computed
    private get status() {
        return this.manifestsStore.manifestStatuses.get(this.props.manifest.softwareUpdateManifestGuid);
    }

    @computed
    private get updating() {
        return !!this.status && this.status.updating > 0;
    }

    @computed
    private get progressTooltip() {
        if (!this.status) return 'Idle';

        const { idle, scheduled, updating, updated, errored, noConnections } = this.status;
        const tooltip = [];
        if (idle) tooltip.push(`${idle}-Idle`);
        if (scheduled) tooltip.push(`${scheduled}-Scheduled`);
        if (updating) tooltip.push(`${updating}-Updating`);
        if (updated) tooltip.push(`${updated}-Updated`);
        if (errored) tooltip.push(`${errored}-Errored`);
        if (noConnections) tooltip.push(`${noConnections}-No Connection`);

        return tooltip.join(', ');
    }

    @bind
    private async deactivate(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        try {
            this.manifestsStore.deactivateAsync(this.props.manifest.softwareUpdateManifestGuid);
        } catch (e: any) {
            console.error(e);
            this.appStore.alertHandle.setError(e);
        }
    }

    @bind
    private async activate(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        try {
            this.manifestsStore.activateAsync(this.props.manifest.softwareUpdateManifestGuid);
        } catch (e: any) {
            console.error(e);
            this.appStore.alertHandle.setError(e);
        }
    }

    @bind
    private async delete(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.confirmModalHandle.open();

        if (await this.confirmModalHandle.confirm()) {
            try {
                this.manifestsStore.deleteAsync(this.props.manifest.softwareUpdateManifestGuid);
            } catch (e: any) {
                console.error(e);
                this.appStore.alertHandle.setError(e);
            }
        }
    }

    @bind
    private async updateNow(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        const task = this.tasksStore.startSessionTask('Start Update');
        try {
            await this.manifestsStore.updateNowAsync(this.props.manifest.softwareUpdateManifestGuid);
            task.complete();
        } catch (e: any) {
            console.error(e);
            task.error(e);
        }
    }

    private handle?: IDisposable;
    private confirmModalHandle = new ModalHandle();
    @observable private loading = false;
    @inject
    private manifestsStore!: ManifestsStore;
    @inject
    private fileSystemsStore!: FileSystemsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
}
